// Pomodoro V2 — dois relógios independentes:
// 1. CICLO (25/5): ritmo. Nunca é interrompido por conclusão de aula.
// 2. AULA (cronômetro de parede): tempo real no conteúdo atual. Começa no primeiro
//    play e SÓ para no CONCLUIR AULA — pausas de ciclo, pausas manuais e anotações
//    contam como tempo de aula (pedido do CEO: o dado real inclui as pausas).
//    Ancorado em timestamp: imune ao throttling de setInterval em aba background.
// Focus score (1-5) é perguntado no FIM do ciclo de foco (momento natural de pausa)
// e aplicado às aulas concluídas naquele ciclo.

import type { Content, Session } from '@app-types/domain';
import { toLocalDateIso, todayLocalIso } from '@core/dates';
import { formatDuration } from '@core/duration';
import { contentActions, sessionActions } from '@core/store';
import { notify } from '@services/notifications';
import { Modal } from '@ui/components/Modal';
import { toast } from '@ui/components/Toast';
import { navigate, useRoute } from '@ui/lib/router';
import { useStore } from '@ui/lib/useStore';
import { useEffect, useRef, useState } from 'preact/hooks';

type Phase = 'focus' | 'short_break' | 'long_break';

function fmt(sec: number): string {
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** cronômetro com horas quando passa de 1h: 1:02:37 */
function fmtClock(sec: number): string {
  const s = Math.max(0, sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function playBeep() {
  try {
    const ctx = new (
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    )();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.15;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    setTimeout(() => {
      osc.stop();
      ctx.close();
    }, 500);
  } catch {
    /* ignore */
  }
}

// (usa helpers de @core/dates — comparação de dia sempre no fuso local)

// ===== PERSISTÊNCIA DO TIMER =====
// F5 / trocar de aba / fechar o browser não pode zerar o pomodoro.
// Âncoras em relógio de parede: restaurar recalcula o tempo decorrido de verdade.
const PERSIST_KEY = 'robox_pomodoro_v1';
const PERSIST_MAX_AGE_MS = 12 * 3600_000; // estado de ontem não interessa

interface PersistedPomodoro {
  phase: Phase;
  cycles: number;
  running: boolean;
  remainingSec: number;
  /** epoch ms do fim do ciclo quando rodando (recalcula remaining no restore) */
  cycleEndAt: number | null;
  currentSessionId?: string;
  adHocContentId?: string;
  lessonStartAt: number | null;
  contentElapsedSec: number;
  pendingScoreIds: string[];
  savedAt: number;
}

function loadPersisted(): PersistedPomodoro | null {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PersistedPomodoro;
    if (Date.now() - p.savedAt > PERSIST_MAX_AGE_MS) return null;
    return p;
  } catch {
    return null;
  }
}

export function PomodoroScreen() {
  const { param } = useRoute();
  const contents = useStore((s) => s.contents);
  const sessions = useStore((s) => s.sessions);
  const settings = useStore((s) => s.settings);

  const focusMin = settings.pomodoro_focus_min;
  const shortBreakMin = settings.pomodoro_short_break_min;
  const longBreakMin = settings.pomodoro_long_break_min;
  const cyclesBeforeLong = settings.pomodoro_cycles_before_long;

  // ===== FILA DO DIA =====
  const tIso = todayLocalIso();
  const todayQueue = sessions
    .filter(
      (s) =>
        toLocalDateIso(s.planned_at) === tIso && (s.status === 'planned' || s.status === 'active'),
    )
    .sort((a, b) => a.planned_at.localeCompare(b.planned_at));

  const doneToday = sessions.filter(
    (s) => s.status === 'completed' && s.completed_at && toLocalDateIso(s.completed_at) === tIso,
  );
  const doneTodayMin = doneToday.reduce((sum, s) => sum + (s.actual_duration_min || 0), 0);

  // estado salvo (F5/navegação) — lido uma vez no mount
  const [persisted] = useState<PersistedPomodoro | null>(loadPersisted);
  // navegou com ?param apontando pra OUTRA sessão → o restore da aula não vale
  const paramOverrides = !!param && param !== persisted?.currentSessionId;

  // ===== RELÓGIO 1: CICLO =====
  const [phase, setPhase] = useState<Phase>(persisted?.phase ?? 'focus');
  const [running, setRunning] = useState(persisted?.running ?? false);
  const [remainingSec, setRemainingSec] = useState(() => {
    if (persisted?.running && persisted.cycleEndAt !== null) {
      return Math.max(0, Math.round((persisted.cycleEndAt - Date.now()) / 1000));
    }
    return persisted?.remainingSec ?? focusMin * 60;
  });
  const [cycles, setCycles] = useState(persisted?.cycles ?? 0);

  // ===== RELÓGIO 2: AULA (cronômetro) =====
  const [currentSessionId, setCurrentSessionId] = useState<string | undefined>(() => {
    if (param && todayQueue.some((s) => s.id === param)) return param;
    if (persisted?.currentSessionId && todayQueue.some((s) => s.id === persisted.currentSessionId))
      return persisted.currentSessionId;
    return todayQueue[0]?.id;
  });
  const [adHocContentId, setAdHocContentId] = useState<string | undefined>(
    paramOverrides ? undefined : persisted?.adHocContentId,
  );
  const [contentElapsedSec, setContentElapsedSec] = useState(
    paramOverrides ? 0 : (persisted?.contentElapsedSec ?? 0),
  );
  /** âncora do cronômetro da aula (epoch ms) — null = ainda sem play nesta aula ou pausada */
  const [lessonStartAt, setLessonStartAt] = useState<number | null>(
    paramOverrides ? null : (persisted?.lessonStartAt ?? null),
  );

  // ===== FOCUS SCORE pendente =====
  const [pendingScoreIds, setPendingScoreIds] = useState<string[]>(
    persisted?.pendingScoreIds ?? [],
  );
  const [askFocus, setAskFocus] = useState<null | 'cycle' | 'leave'>(null);

  const currentSession: Session | undefined = currentSessionId
    ? sessions.find((s) => s.id === currentSessionId)
    : undefined;
  const currentContent: Content | undefined = currentSession
    ? contents.find((c) => c.id === currentSession.content_id)
    : adHocContentId
      ? contents.find((c) => c.id === adHocContentId)
      : undefined;

  // atalho global ESPAÇO → play/pause (evento disparado pelo App)
  const toggleRef = useRef<() => void>(() => {});
  toggleRef.current = () => {
    if (running) pauseCycle();
    else startFocus();
  };
  useEffect(() => {
    const onToggle = () => toggleRef.current();
    window.addEventListener('robox:toggle-timer', onToggle);
    return () => window.removeEventListener('robox:toggle-timer', onToggle);
  }, []);

  // fallback: se a sessão atual sumiu/concluiu por fora, pega a primeira da fila
  useEffect(() => {
    const cur = currentSessionId ? sessions.find((s) => s.id === currentSessionId) : undefined;
    const curValid = cur && (cur.status === 'planned' || cur.status === 'active');
    if (!curValid && !adHocContentId && todayQueue.length > 0) {
      setCurrentSessionId(todayQueue[0].id);
    }
  }, [sessions.length, currentSessionId, adHocContentId, todayQueue[0]?.id]);

  // ===== TICK DO CICLO — ancorado em relógio de parede (imune a throttling) =====
  const remainingRef = useRef(remainingSec);
  remainingRef.current = remainingSec;
  const cycleEndAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!running) {
      cycleEndAtRef.current = null;
      return;
    }
    // (re)ancora ao dar play ou trocar de fase com o timer rodando
    cycleEndAtRef.current = Date.now() + remainingRef.current * 1000;
    const int = window.setInterval(() => {
      if (cycleEndAtRef.current === null) return;
      setRemainingSec(Math.max(0, Math.round((cycleEndAtRef.current - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(int);
  }, [running, phase]);

  // ===== PERSISTE O TIMER (F5/navegação não zera nada) =====
  useEffect(() => {
    const data: PersistedPomodoro = {
      phase,
      cycles,
      running,
      remainingSec,
      cycleEndAt: running ? cycleEndAtRef.current : null,
      currentSessionId,
      adHocContentId,
      lessonStartAt,
      contentElapsedSec,
      pendingScoreIds,
      savedAt: Date.now(),
    };
    try {
      localStorage.setItem(PERSIST_KEY, JSON.stringify(data));
    } catch {
      /* quota cheia — sem persistência, mas o timer segue */
    }
  }, [
    phase,
    cycles,
    running,
    remainingSec,
    currentSessionId,
    adHocContentId,
    lessonStartAt,
    contentElapsedSec,
    pendingScoreIds,
  ]);

  // ===== TICK DA AULA — derivado do relógio de parede, roda até CONCLUIR =====
  useEffect(() => {
    if (lessonStartAt === null) return;
    const update = () => setContentElapsedSec(Math.floor((Date.now() - lessonStartAt) / 1000));
    update();
    const int = window.setInterval(update, 1000);
    return () => clearInterval(int);
  }, [lessonStartAt]);

  // título da aba (útil com a aba em background no desktop)
  useEffect(() => {
    if (running) {
      document.title = `${fmt(remainingSec)} · ${phase === 'focus' ? 'FOCO' : 'PAUSA'} — Robox`;
    } else {
      document.title = 'Robox';
    }
    return () => {
      document.title = 'Robox';
    };
  }, [running, remainingSec, phase]);

  // ===== TRANSIÇÃO DE FASE =====
  useEffect(() => {
    if (remainingSec > 0) return;
    if (phase === 'focus') {
      playBeep();
      const nextCycles = cycles + 1;
      setCycles(nextCycles);
      const useLong = nextCycles % cyclesBeforeLong === 0;
      setPhase(useLong ? 'long_break' : 'short_break');
      setRemainingSec((useLong ? longBreakMin : shortBreakMin) * 60);
      setRunning(settings.auto_start_break);
      toast(useLong ? 'Ciclo completo — pausa longa!' : 'Ciclo completo — pausa curta!', 'ok');
      if (document.hidden)
        notify('✓ Ciclo completo', useLong ? 'Pausa longa — descansa de verdade.' : 'Pausa curta!');
      if (pendingScoreIds.length > 0) setAskFocus('cycle');
    } else {
      playBeep();
      setPhase('focus');
      setRemainingSec(focusMin * 60);
      setRunning(false);
      toast('Pausa acabou. Pronto pro próximo ciclo.', 'info');
      if (document.hidden) notify('▶ Pausa acabou', 'Pronto pro próximo ciclo de foco.');
    }
  }, [remainingSec, phase, cycles]);

  // ===== AÇÕES: CICLO =====
  /** marca a sessão como iniciada no store + liga o cronômetro da aula */
  function beginLesson() {
    if (!currentContent) return;
    if (currentSession && currentSession.status === 'planned') {
      sessionActions.start(currentSession.id);
    } else if (!currentSession && adHocContentId) {
      const sess = sessionActions.create(
        adHocContentId,
        new Date().toISOString(),
        currentContent.duration_min,
        currentContent.goalId,
      );
      sessionActions.start(sess.id);
      setCurrentSessionId(sess.id);
      setAdHocContentId(undefined);
    }
    // liga o cronômetro da aula no primeiro play (preserva tempo já medido)
    if (lessonStartAt === null) {
      setLessonStartAt(Date.now() - contentElapsedSec * 1000);
    }
  }

  function startFocus() {
    if (phase === 'focus') beginLesson();
    setRunning(true);
  }

  /**
   * Pausa MANUAL do usuário: congela ciclo E cronômetro da aula.
   * (Transições automáticas de fase — fim do foco, fim da pausa — NÃO
   * passam por aqui de propósito: a aula continua contando nelas.)
   */
  function pauseCycle() {
    setRunning(false);
    if (lessonStartAt !== null) {
      // snapshot exato antes de soltar a âncora
      setContentElapsedSec(Math.floor((Date.now() - lessonStartAt) / 1000));
      setLessonStartAt(null);
    }
  }

  /** em pausa com aula na fila: pula a pausa e já entra em foco nesta aula */
  function startLessonNow() {
    setPhase('focus');
    setRemainingSec(focusMin * 60);
    beginLesson();
    setRunning(true);
    toast('Pausa pulada — foco na próxima aula.', 'info');
  }

  function resetCycle() {
    if (!confirm('Resetar o ciclo atual? (o cronômetro da aula não é afetado)')) return;
    setRunning(false);
    setPhase('focus');
    setRemainingSec(focusMin * 60);
  }

  function skipBreak() {
    setPhase('focus');
    setRemainingSec(focusMin * 60);
    setRunning(false);
    toast('Pausa pulada.', 'info');
  }

  // ===== AÇÕES: AULA =====
  function nextInQueue(excludeId?: string): Session | undefined {
    return todayQueue.find((s) => s.id !== excludeId);
  }

  function completeLesson() {
    if (!currentContent) return;
    const actualMin = Math.max(1, Math.round(contentElapsedSec / 60));

    let completedId: string;
    if (currentSession) {
      if (currentSession.status === 'planned') sessionActions.start(currentSession.id);
      sessionActions.complete(currentSession.id, actualMin);
      completedId = currentSession.id;
    } else if (adHocContentId) {
      const sess = sessionActions.create(
        adHocContentId,
        new Date().toISOString(),
        currentContent.duration_min,
        currentContent.goalId,
      );
      sessionActions.start(sess.id);
      sessionActions.complete(sess.id, actualMin);
      contentActions.update(adHocContentId, { status: 'done' });
      completedId = sess.id;
      setAdHocContentId(undefined);
    } else {
      return;
    }

    setPendingScoreIds((prev) => [...prev, completedId]);
    setContentElapsedSec(0);

    const next = nextInQueue(completedId);
    setCurrentSessionId(next?.id);
    // próxima aula: se o ciclo segue rodando em foco, ela já começa (cronômetro + store)
    const autoContinue = !!next && running && phase === 'focus';
    setLessonStartAt(autoContinue ? Date.now() : null);
    if (autoContinue && next && next.status === 'planned') {
      sessionActions.start(next.id);
    }
    toast(
      next
        ? `✓ Aula concluída (${actualMin}min) → próxima na fila`
        : `✓ Aula concluída (${actualMin}min) · fila do dia zerada!`,
      'ok',
      3500,
    );
    // ciclo continua rodando — é o ponto central do design
  }

  function switchTo(sessionId: string) {
    if (sessionId === currentSessionId) return;
    if (contentElapsedSec >= 60) {
      if (
        !confirm(
          `Você já estudou ${fmtClock(contentElapsedSec)} nesta aula sem concluir. Trocar descarta essa medição. Continuar?`,
        )
      )
        return;
    }
    setCurrentSessionId(sessionId);
    setAdHocContentId(undefined);
    setContentElapsedSec(0);
    setLessonStartAt(running && phase === 'focus' ? Date.now() : null);
  }

  function leave() {
    if (pendingScoreIds.length > 0) {
      setAskFocus('leave');
    } else {
      navigate('home');
    }
  }

  function answerFocus(score?: number) {
    if (score !== undefined) {
      sessionActions.setFocus(pendingScoreIds, score);
    }
    setPendingScoreIds([]);
    const wasLeave = askFocus === 'leave';
    setAskFocus(null);
    if (wasLeave) navigate('home');
  }

  // ===== RENDER =====
  const phaseSeconds =
    phase === 'focus'
      ? focusMin * 60
      : phase === 'short_break'
        ? shortBreakMin * 60
        : longBreakMin * 60;
  const progress = Math.min(1, Math.max(0, 1 - remainingSec / phaseSeconds));

  const R = 60;
  const CIRC = 2 * Math.PI * R;
  const phaseColor =
    phase === 'focus' ? 'var(--cyan)' : phase === 'short_break' ? 'var(--green)' : 'var(--purple)';

  const predictedSec = currentContent ? Math.round(currentContent.duration_min * 60) : 0;
  const lessonProgress = predictedSec > 0 ? contentElapsedSec / predictedSec : 0;
  const overBudget = lessonProgress > 1;

  const pendingContents = contents.filter((c) => c.status === 'todo' || c.status === 'doing');
  const totalTodaySec = doneTodayMin * 60 + contentElapsedSec;

  const cycleDots = Array.from(
    { length: cyclesBeforeLong },
    (_, i) => i < cycles % cyclesBeforeLong,
  );

  return (
    <div>
      <div class="main-head">
        <div>
          <h1 class="main-title">Pomodoro</h1>
          <div class="main-sub">
            {phase === 'focus' ? 'FOCO' : phase === 'short_break' ? 'PAUSA CURTA' : 'PAUSA LONGA'} ·
            CICLO {cycles + 1}
          </div>
        </div>
        <div class="main-actions">
          <span
            class="tag green"
            style={{ fontSize: '12px', padding: '8px 14px' }}
            title="tempo estudado hoje (concluído + em andamento)"
          >
            HOJE: {formatDuration(totalTodaySec / 60)}
          </span>
          <button class="btn ghost" onClick={leave}>
            ← VOLTAR
          </button>
        </div>
      </div>

      <div class="split wide-right">
        {/* ===== COL 1: CICLO ===== */}
        <div class="panel" style={{ textAlign: 'center', padding: '28px 24px' }}>
          <div
            style={{
              fontFamily: 'var(--f-mono)',
              fontSize: '11px',
              color: phaseColor,
              letterSpacing: '3px',
              marginBottom: '12px',
            }}
          >
            {phase === 'focus'
              ? '▰ CICLO DE FOCO'
              : phase === 'short_break'
                ? '▰ PAUSA CURTA'
                : '▰ PAUSA LONGA'}
          </div>

          <div
            style={{ position: 'relative', width: '230px', height: '230px', margin: '0 auto 18px' }}
          >
            <svg
              viewBox="0 0 160 160"
              style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}
            >
              <circle
                cx="80"
                cy="80"
                r={R}
                fill="none"
                stroke="rgba(0,240,255,0.08)"
                stroke-width="9"
              />
              <circle
                cx="80"
                cy="80"
                r={R}
                fill="none"
                stroke={phaseColor}
                stroke-width="9"
                stroke-linecap="round"
                stroke-dasharray={CIRC}
                stroke-dashoffset={CIRC * (1 - progress)}
                style={{ transition: 'stroke-dashoffset 0.5s linear' }}
              />
            </svg>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--f-heading)',
                  fontSize: 'clamp(36px, 9vw, 46px)',
                  fontWeight: 900,
                  color: phaseColor,
                  letterSpacing: '2px',
                  lineHeight: 1,
                }}
              >
                {fmt(remainingSec)}
              </div>
              <div
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: '10px',
                  color: 'var(--text-dim)',
                  letterSpacing: '2px',
                  marginTop: '8px',
                }}
              >
                {phase === 'focus' ? `${focusMin} MIN DE RITMO` : 'DESCANSA DE VERDADE'}
              </div>
            </div>
          </div>

          {/* dots de ciclos até a pausa longa */}
          <div
            style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '18px' }}
          >
            {cycleDots.map((done, i) => (
              <span
                key={String(i)}
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: done ? 'var(--cyan)' : 'rgba(0,240,255,0.15)',
                  boxShadow: done ? '0 0 8px rgba(0,240,255,0.6)' : 'none',
                }}
              />
            ))}
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {!running ? (
              <button class="btn primary lg" onClick={startFocus}>
                ▶ {phase === 'focus' ? 'FOCAR' : 'INICIAR PAUSA'}
              </button>
            ) : (
              <button class="btn lg" onClick={pauseCycle}>
                ⏸ PAUSAR
              </button>
            )}
            {phase !== 'focus' && (
              <button class="btn ghost lg" onClick={skipBreak}>
                ⏭ PULAR PAUSA
              </button>
            )}
            <button class="btn ghost lg" onClick={resetCycle}>
              ↺
            </button>
          </div>

          <div
            style={{
              marginTop: '20px',
              paddingTop: '14px',
              borderTop: '1px dashed var(--border-soft)',
              fontFamily: 'var(--f-mono)',
              fontSize: '10.5px',
              color: 'var(--text-dim)',
              lineHeight: 1.7,
            }}
          >
            {focusMin}min foco · {shortBreakMin}min pausa · longa de {longBreakMin}min a cada{' '}
            {cyclesBeforeLong} ciclos
            <br />
            <button
              class="btn sm ghost"
              style={{ marginTop: '6px' }}
              onClick={() => navigate('settings')}
            >
              ⚙ AJUSTAR
            </button>
          </div>
        </div>

        {/* ===== COL 2: AULA ATUAL + FILA ===== */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div class="panel">
            <div class="panel-title">
              ▰ <b>ESTUDANDO AGORA</b>
              {currentContent && (
                <span
                  style={{
                    fontFamily: 'var(--f-mono)',
                    fontSize: '10px',
                    color: 'var(--text-dim)',
                  }}
                >
                  o ciclo NÃO para ao concluir
                </span>
              )}
            </div>

            {currentContent ? (
              <div>
                <div
                  style={{
                    fontSize: '17px',
                    color: 'var(--text)',
                    fontWeight: 600,
                    lineHeight: 1.35,
                  }}
                >
                  {currentContent.title}
                </div>
                <div
                  style={{ display: 'flex', gap: '8px', margin: '8px 0 16px', flexWrap: 'wrap' }}
                >
                  <span class="tag">{currentContent.type}</span>
                  <span class="tag purple">
                    previsto: {formatDuration(currentContent.duration_min)}
                  </span>
                  <span class="tag amber">dif. {currentContent.difficulty}</span>
                </div>

                {/* cronômetro da aula */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '14px',
                    padding: '14px 18px',
                    background: 'rgba(0,8,24,0.55)',
                    border: '1px solid var(--border-soft)',
                    borderRadius: 'var(--r-md)',
                    marginBottom: '10px',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'var(--f-heading)',
                      fontSize: 'clamp(30px, 7vw, 38px)',
                      fontWeight: 900,
                      color: overBudget ? 'var(--amber)' : 'var(--green)',
                      lineHeight: 1,
                      letterSpacing: '1px',
                    }}
                  >
                    {fmtClock(contentElapsedSec)}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--f-mono)',
                      fontSize: '11px',
                      color: 'var(--text-dim)',
                    }}
                  >
                    NESTA AULA
                    {lessonStartAt !== null
                      ? ' · rodando (para só no concluir)'
                      : contentElapsedSec > 0
                        ? ' · pausada'
                        : ' · aguardando play'}
                  </div>
                </div>

                {/* barra tempo real vs. previsto */}
                <div
                  style={{
                    height: '8px',
                    background: 'rgba(0,8,24,0.6)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-soft)',
                    marginBottom: '6px',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(100, lessonProgress * 100)}%`,
                      background: overBudget
                        ? 'linear-gradient(90deg, var(--amber), var(--red))'
                        : 'linear-gradient(90deg, var(--green), var(--cyan))',
                      transition: 'width 1s linear',
                    }}
                  />
                </div>
                <div
                  style={{
                    fontFamily: 'var(--f-mono)',
                    fontSize: '10.5px',
                    color: overBudget ? 'var(--amber)' : 'var(--text-dim)',
                    marginBottom: '16px',
                  }}
                >
                  {overBudget
                    ? `⚠ ${Math.round(lessonProgress * 100)}% do previsto — sem stress, o dado real é o que vale`
                    : `${Math.round(lessonProgress * 100)}% do tempo previsto`}
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button class="btn green lg" onClick={completeLesson}>
                    ✓ CONCLUIR AULA
                  </button>
                  {!running && phase === 'focus' && (
                    <button class="btn primary lg" onClick={startFocus}>
                      ▶ FOCAR
                    </button>
                  )}
                  {phase !== 'focus' && lessonStartAt === null && (
                    <button class="btn primary lg" onClick={startLessonNow}>
                      ▶ PRÓXIMA AULA AGORA
                    </button>
                  )}
                </div>
              </div>
            ) : todayQueue.length === 0 && pendingContents.length > 0 ? (
              <div>
                <div class="empty-state" style={{ padding: '10px 0 16px' }}>
                  Fila do dia zerada ✓ — quer adiantar algo?
                </div>
                <div class="field">
                  <label>Estudar avulso (fora do plano)</label>
                  <select
                    class="select"
                    value={adHocContentId || ''}
                    onChange={(e) => {
                      setAdHocContentId((e.target as HTMLSelectElement).value || undefined);
                      setContentElapsedSec(0);
                      setLessonStartAt(null);
                    }}
                  >
                    <option value="">— escolher conteúdo —</option>
                    {pendingContents.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title} ({formatDuration(c.duration_min)})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <div class="empty-state" style={{ padding: '20px 0' }}>
                {pendingContents.length === 0
                  ? 'Nenhum conteúdo pendente. Adicione em Conteúdos.'
                  : 'Nenhuma sessão pra hoje. Gere um plano na Home ou escolha avulso acima.'}
              </div>
            )}
          </div>

          {/* FILA DE HOJE */}
          <div class="panel">
            <div class="panel-title">
              ▰ <b>FILA DE HOJE</b>
              <span style={{ fontFamily: 'var(--f-mono)', fontSize: '10px' }}>
                {doneToday.length} feitas · {todayQueue.length} restantes
              </span>
            </div>
            {todayQueue.length === 0 ? (
              <div class="empty-state" style={{ padding: '12px 0' }}>
                {doneToday.length > 0
                  ? 'Tudo feito por hoje 🔥'
                  : 'Sem sessões planejadas pra hoje.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {todayQueue.slice(0, 6).map((s) => {
                  const c = contents.find((x) => x.id === s.content_id);
                  const isCurrent = s.id === currentSessionId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => switchTo(s.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '9px 12px',
                        borderRadius: 'var(--r-sm)',
                        background: isCurrent ? 'rgba(0,240,255,0.08)' : 'rgba(0,8,24,0.4)',
                        border: `1px solid ${isCurrent ? 'var(--cyan)' : 'var(--border-soft)'}`,
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                        transition: 'all var(--tr-fast)',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'var(--f-mono)',
                          fontSize: '10px',
                          color: isCurrent ? 'var(--cyan)' : 'var(--text-dim)',
                          minWidth: '20px',
                        }}
                      >
                        {isCurrent ? '▶' : '○'}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          fontSize: '13.5px',
                          color: isCurrent ? 'var(--text)' : 'var(--text-soft)',
                          fontWeight: isCurrent ? 600 : 400,
                        }}
                      >
                        {c?.title || '—'}
                      </span>
                      <span class="tag purple" style={{ fontSize: '10px' }}>
                        {formatDuration(s.planned_duration_min)}
                      </span>
                    </button>
                  );
                })}
                {todayQueue.length > 6 && (
                  <div
                    style={{
                      fontFamily: 'var(--f-mono)',
                      fontSize: '10px',
                      color: 'var(--text-dim)',
                      textAlign: 'center',
                      padding: '4px',
                    }}
                  >
                    +{todayQueue.length - 6} depois dessas
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== PROMPT DE FOCO (fim de ciclo / saída) ===== */}
      <Modal
        open={askFocus !== null}
        onClose={() => answerFocus(undefined)}
        title={
          askFocus === 'cycle'
            ? 'Como foi seu foco neste ciclo?'
            : 'Antes de sair: como foi o foco?'
        }
        maxWidth={440}
      >
        <div
          style={{
            fontFamily: 'var(--f-mono)',
            fontSize: '11px',
            color: 'var(--text-dim)',
            marginBottom: '16px',
            lineHeight: 1.6,
          }}
        >
          Vale para as {pendingScoreIds.length} aula(s) concluída(s) neste bloco. Alimenta a
          dimensão ENERGIA do seu Score. 1 clique.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => answerFocus(n)}
              style={{
                padding: '16px 8px',
                background: 'rgba(0,8,24,0.6)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                transition: 'all var(--tr-fast)',
                color: 'var(--text)',
              }}
              onMouseOver={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--cyan)';
                (e.currentTarget as HTMLButtonElement).style.boxShadow =
                  '0 0 12px rgba(0,240,255,0.3)';
              }}
              onMouseOut={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
                (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: '22px' }}>{['😴', '😕', '😐', '🙂', '🔥'][n - 1]}</span>
              <span style={{ fontFamily: 'var(--f-heading)', fontSize: '14px', fontWeight: 700 }}>
                {n}
              </span>
            </button>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '14px' }}>
          <button class="btn ghost sm" onClick={() => answerFocus(undefined)}>
            PULAR →
          </button>
        </div>
      </Modal>
    </div>
  );
}
